import React, { useState, useEffect } from 'react';
import { X, Mail, Phone, MapPin, Calendar, Edit } from 'lucide-react';
import { Button } from '../../../components/ui/button'; 
import { Badge } from '../../../components/ui/badge';
import { Avatar, AvatarFallback } from '../../../components/ui/avatar';
import { Separator } from '../../../components/ui/separator';
import { motion, AnimatePresence } from 'framer-motion';
// import { updateExamCandidateDetails } from '../../../services/usersapi'; // Adjusted API call
import { toast } from 'react-hot-toast';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../../components/ui/alert-dialog';

interface Validation {
  isCorrect: boolean;
  score: number;
  feedback: string;
  _id: string;
}

interface ExamCandidate {
  _id: string;
  fullName: string;
  email: string;
  phone: string;
  college: string;
  questions: string[];
  answers: string[];
  totalScore: number;
  status: string;
  neuralPatterns: string;
  validations: Validation[];
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface UserDetailSidebarProps {
  user: ExamCandidate | null;
  onClose: () => void;
  onUserUpdate?: (updatedUser: ExamCandidate) => void;
  // setUserEdited: (edited: boolean) => void;
}

interface EditExamData {
  fullName: string;
  college: string;
  phone: string;
}

const sidebarVariants = {
  hidden: { x: '100%', opacity: 0 },
  visible: { x: 0, opacity: 1 },
  exit: { x: '100%', opacity: 0 },
};

const ExamUserDetailSidebar: React.FC<UserDetailSidebarProps> = ({ user, onClose, }) => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editData, setEditData] = useState<EditExamData>({
    fullName: user?.fullName || '',
    college: user?.college || '',
    phone: user?.phone || '',
  });

  useEffect(() => {
    if (user) {
      setEditData({
        fullName: user.fullName,
        college: user.college || '',
        phone: user.phone || '',
      });
    }
  }, [user]);

  if (!user) return null;

  const getStatusBadgeColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'validated':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'questions_generated':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const validatePhoneNumber = (phone: string): boolean => {
    const phoneRegex = /^\d{10}$/;
    return phoneRegex.test(phone);
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      if (value !== '' && !/^\d*$/.test(value)) {
        return;
      }
      if (value.length > 10) {
        return;
      }
    }
    setEditData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveEdit = async () => {
    if (!validatePhoneNumber(editData.phone)) {
      toast.error("Phone number must be exactly 10 digits");
      return;
    }

    // try {
    //   const response = await updateExamCandidateDetails(user._id, editData);
    //   toast.success('Candidate details updated successfully');
    //   setIsEditOpen(false);
    //   if (onUserUpdate && response.data.user) {
    //     onUserUpdate(response.data.user as ExamCandidate);
    //   }
    // } catch (error: any) {
    //   if (error.response?.status === 409) {
    //     toast.error("This phone number is already associated with another account");
    //   } else {
    //     toast.error('Failed to update candidate details');
    //   }
    // }
  };

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={user._id}
        variants={sidebarVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="fixed top-0 right-0 h-full w-96 bg-white border-l border-border z-50 shadow-xl"
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <h2 className="text-lg font-semibold text-foreground">Candidate Details</h2>
            <Button variant="ghost" size="sm" onClick={onClose} className="w-8 h-8 p-0">
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Candidate Profile */}
            <div className="flex items-center gap-4">
              <Avatar className="w-16 h-16">
                <AvatarFallback className="text-lg">
                  {user.fullName?.slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div>
                <h3 className="text-xl font-semibold text-foreground">{user.fullName}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={getStatusBadgeColor(user.status)}>{user.status}</Badge>
                </div>
              </div>
            </div>

            <Separator />

            {/* Contact Information */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3 flex items-center justify-between">
                Contact Information
                
              </h4>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-foreground">{user.email}</p>
                    <p className="text-xs text-muted-foreground">Email Address</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-foreground">{user.phone}</p>
                    <p className="text-xs text-muted-foreground">Phone Number</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-foreground">{user.college || 'Not specified'}</p>
                    <p className="text-xs text-muted-foreground">College</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-foreground">{new Date(user.createdAt).toLocaleDateString()}</p>
                    <p className="text-xs text-muted-foreground">Exam Date</p>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Exam Details */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">Exam Details</h4>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-foreground">{new Date(user.updatedAt).toLocaleDateString()}</p>
                    <p className="text-xs text-muted-foreground">Last Updated</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-foreground">{user.totalScore || 'N/A'}</p>
                    <p className="text-xs text-muted-foreground">Total Score</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-foreground">{user.neuralPatterns || 'N/A'}</p>
                    <p className="text-xs text-muted-foreground">Neural Patterns</p>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Questions and Answers */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">Questions & Answers (Q & A:)</h4>
              <div className="space-y-3">
                <div>
  {/* <p className="text-sm font-medium text-foreground mb-2">Q & A:</p> */}
  {user.questions.map((q, index) => (
    <div key={index} className="mb-2">
      <p className="text-sm font-semibold text-foreground">
        {index + 1}. Q: {q}
      </p>
      <p className="text-sm text-foreground ml-4">
        A: {user.answers[index] || "N/A"}
      </p>
    </div>
  ))}
</div>

              </div>
            </div>

            <Separator />

            {/* Validations */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">Validations</h4>
              {user.validations.length > 0 ? (
                <div className="space-y-3">
                  {user.validations.map((v, index) => (
                    <div key={index} className="text-sm">
                      <p><span className="font-semibold"> Correct:</span> {v.isCorrect ? 'Yes' : 'No'}</p>
                      <p><span className="font-semibold">Score: </span>{v.score}</p>
                      <p><span className="font-semibold">Feedback:</span> {v.feedback || 'N/A'}</p>
                      <hr className="my-2" />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No validations available</p>
              )}
            </div>
          </div>

          {/* Actions */}
         

          {/* Edit Popup */}
          <AnimatePresence>
            {isEditOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"
                onClick={() => setIsEditOpen(false)}
              >
                <motion.div
                  initial={{ y: 50, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 50, opacity: 0 }}
                  className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md"
                  onClick={e => e.stopPropagation()}
                >
                  <h3 className="text-lg font-semibold mb-4">
                    Edit Candidate Details for <span className="text-[#EC6E51]">{user.fullName}</span>
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground">Full Name</label>
                      <input
                        type="text"
                        name="fullName"
                        value={editData.fullName}
                        onChange={handleEditChange}
                        className="w-full p-2 border border-border rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground">College</label>
                      <input
                        type="text"
                        name="college"
                        value={editData.college}
                        onChange={handleEditChange}
                        className="w-full p-2 border border-border rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground">Phone</label>
                      <input
                        type="text"
                        name="phone"
                        value={editData.phone}
                        onChange={handleEditChange}
                        className="w-full p-2 border border-border rounded-md"
                      />
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setIsEditOpen(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleSaveEdit}>Save</Button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ExamUserDetailSidebar;